import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Approx width (px) reserved for the "+N more" pill before the fit loop. Only subtracted when
// overflow actually occurs, so a perfectly-fitting row uses the full width.
export const OVERFLOW_PILL_RESERVE_PX = 84;

export const useChipOverflow = (totalCount: number, measureKey: string, enabled: boolean) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(totalCount);

  const recompute = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const chips = Array.from(measure.children) as HTMLElement[];
    if (chips.length === 0) {
      setVisibleCount(0);
      return;
    }

    const available = container.clientWidth;
    const rightEdges = chips.map((c) => c.offsetLeft + c.offsetWidth);

    if (rightEdges[rightEdges.length - 1] <= available) {
      setVisibleCount(chips.length);
      return;
    }

    const budget = available - OVERFLOW_PILL_RESERVE_PX;

    let count = 0;
    for (let i = 0; i < rightEdges.length; i++) {
      if (rightEdges[i] > budget) break;
      count = i + 1;
    }
    setVisibleCount(Math.max(count, 1));
  }, []);

  useLayoutEffect(() => {
    if (enabled) recompute();
    // measureKey changes when the selected set changes; recompute is stable.
  }, [enabled, measureKey, totalCount, recompute]);

  useEffect(() => {
    if (!enabled || typeof ResizeObserver === 'undefined') return;
    const container = containerRef.current;
    if (!container) return;
    let raf = 0;
    const ro = new ResizeObserver((entries) => {
      // display:none (e.g. hidden filters panel) reports zero size; measuring then would
      // wipe the chip layout and re-expand everything for a frame on reveal.
      if (entries.some((entry) => entry.contentRect.width === 0)) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(recompute);
    });
    ro.observe(container);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [enabled, recompute]);

  return { containerRef, measureRef, visibleCount };
};
